import { Suspense, type ReactNode } from 'react';
import { ListableInfo, LoadingIndicator, Panel } from '@ics-portal/guardian';
import { formatDate } from '@utils/dateFormatUtils';
import { VendorAssessment } from '@components/VendorAssessment/VendorAssessment';
import { ExternalIdentifiers } from '@components/ExternalIdentifiers/ExternalIdentifiers';
import { InfoCardWrapper } from '@components/_molecules/InfoCards';
import { type ComponentDetailsViewModel } from './models/ComponentDetailsViewModel';

interface ComponentDetailsCardProps {
    data: ComponentDetailsViewModel;
}

function renderExternalLink(url: string | null | undefined): ReactNode {
    if (!url) return '-';
    return (
        <a
            href={url}
            target="_blank"
            rel="noreferrer"
            style={{ wordBreak: 'break-all' }}
        >
            {url}
        </a>
    );
}

export function ComponentDetailsCard({
    data,
}: ComponentDetailsCardProps): ReactNode {
    return (
        <InfoCardWrapper title="Details">
            <Panel header={`Component: ${data.name}`}>
                <div className="flex flex-column gap-2 pt-sm pb-lg">
                    <ListableInfo
                        label="Version"
                        value={data.version}
                        orientation="vertical"
                    />
                    <ListableInfo
                        label="EOL date"
                        value={data.eolDate ? formatDate(data.eolDate) : '-'}
                        orientation="vertical"
                    />
                    <ListableInfo
                        label="EOL reached"
                        value={data.eolReached ? 'Yes' : 'No'}
                        orientation="vertical"
                    />
                    <ListableInfo
                        label="Is valid"
                        value={data.isValid ? 'True' : 'False'}
                        orientation="vertical"
                    />
                    <ListableInfo
                        label="Monitored since"
                        value={
                            data.monitoredSince
                                ? formatDate(data.monitoredSince)
                                : '-'
                        }
                        orientation="vertical"
                    />
                    <ListableInfo
                        label="Vendor"
                        value={data.vendorName ?? '-'}
                        orientation="vertical"
                    />
                    <ListableInfo
                        label="URL"
                        value={renderExternalLink(data.url)}
                        orientation="vertical"
                    />
                    <ListableInfo
                        label="Security URL"
                        value={renderExternalLink(data.securityUrl)}
                        orientation="vertical"
                    />
                </div>
            </Panel>
            <Suspense fallback={<LoadingIndicator variant="ring" />}>
                <VendorAssessment componentId={data.id} />
            </Suspense>
            <Panel
                header={
                    <h3 className="text-secondary">External identifiers</h3>
                }
            >
                <Suspense fallback={<LoadingIndicator variant="ring" />}>
                    <ExternalIdentifiers
                        componentId={data.id}
                        icsPortalSource={data.cpeName}
                    />
                </Suspense>
            </Panel>
        </InfoCardWrapper>
    );
}
